<script setup lang="ts">
import type { Category, DealCard } from '#shared/types/deal'
import type { PaginatedResponse } from '#shared/types/api'

definePageMeta({ layout: 'default' })

const route = useRoute()
const slug = computed(() => route.params.slug as string)
const config = useRuntimeConfig()
const { trackEvent } = useUmami()
const pageUrl = computed(() => `${config.public.appUrl}${route.path}`)
const defaultShareImage = `${config.public.appUrl}/og-default.png`

const { data: category } = await useFetch<Category>(`/categories/${slug.value}`, {
  baseURL: config.public.apiBaseUrl,
  key: `category-${slug.value}`,
})

const seoTitle = computed(() =>
  category.value
    ? `${category.value.name} em Oferta — Promoções e Descontos | Descontito`
    : 'Categoria — Descontito',
)

const seoDescription = computed(() => {
  if (!category.value) return undefined
  return `Veja ofertas, promoções e descontos de ${category.value.name.toLowerCase()} atualizados no Descontito.`
})

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogType: 'website',
  ogUrl: pageUrl,
  ogImage: defaultShareImage,
  ogImageAlt: computed(() =>
    category.value
      ? `Ofertas da categoria ${category.value.name} no Descontito`
      : 'Ofertas por categoria no Descontito',
  ),
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: defaultShareImage,
  twitterImageAlt: computed(() =>
    category.value
      ? `Ofertas da categoria ${category.value.name} no Descontito`
      : 'Ofertas por categoria no Descontito',
  ),
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: pageUrl.value,
    },
  ],
  meta: [
    { property: 'og:image:secure_url', content: defaultShareImage },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
  ],
})

const tabs = [
  { key: 'hot', label: '🔥 Em Alta', sort: 'hot' as const },
  { key: 'new', label: '🕐 Recentes', sort: 'new' as const },
  { key: 'commented', label: '💬 Mais Comentadas', sort: 'commented' as const },
]

const activeTab = ref('hot')
const activeOnly = ref(true)

const currentFilters = computed(() => ({
  category: slug.value,
  sort: tabs.find(t => t.key === activeTab.value)?.sort ?? 'hot',
  status: activeOnly.value ? 'ACTIVE' as const : undefined,
  limit: 20,
}))

const { data: initialDeals } = await useFetch<PaginatedResponse<DealCard>>('/deals', {
  baseURL: config.public.apiBaseUrl,
  query: currentFilters.value,
  key: `category-deals-initial-${slug.value}-${activeTab.value}-${activeOnly.value ? 'active' : 'all'}`,
})

const { deals, loadMore, hasMore, loading, reset } = useInfiniteDeals(currentFilters.value, initialDeals.value)
const schemaDeals = computed(() => deals.value.slice(0, 10))

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: computed(() => [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: config.public.appUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Categorias',
        item: `${config.public.appUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.value?.name ?? 'Categoria',
        item: pageUrl.value,
      },
    ]),
  }),
  {
    '@type': 'CollectionPage',
    name: seoTitle,
    description: seoDescription,
    url: pageUrl,
    inLanguage: 'pt-BR',
    about: computed(() =>
      category.value
        ? {
            '@type': 'Thing',
            name: category.value.name,
          }
        : undefined,
    ),
  },
  {
    '@type': 'ItemList',
    name: computed(() =>
      category.value
        ? `Ofertas de ${category.value.name}`
        : 'Ofertas por categoria',
    ),
    numberOfItems: computed(() => schemaDeals.value.length),
    itemListElement: computed(() =>
      schemaDeals.value.map((deal, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${config.public.appUrl}/oferta/${deal.slug}`,
        name: deal.title,
      })),
    ),
  },
])

const sentinel = ref<HTMLElement | null>(null)

onMounted(() => {
  if (!sentinel.value) return
  const observer = new IntersectionObserver(
    ([entry]) => { if (entry?.isIntersecting) loadMore() },
    { rootMargin: '200px' },
  )
  observer.observe(sentinel.value)
  onUnmounted(() => observer.disconnect())
})

watch(activeTab, (value) => {
  trackEvent('deal-sort-change', { page: 'category', category: slug.value, sort: value })
  reset(currentFilters.value)
})

watch(activeOnly, (value) => {
  trackEvent('active-filter-toggle', { page: 'category', category: slug.value, activeOnly: value })
  reset(currentFilters.value)
})
</script>

<template>
  <div>
    <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
      <template v-if="category">
        <span v-if="category.icon">{{ category.icon }}</span>
        {{ category.name }}
      </template>
      <USkeleton v-else class="h-7 w-40" />
    </h1>

    <!-- Tabs + filtro -->
    <div class="flex items-center justify-between gap-2 mb-6 flex-wrap">
      <div class="flex gap-1">
        <UButton
          v-for="tab in tabs"
          :key="tab.key"
          :color="activeTab === tab.key ? 'primary' : 'neutral'"
          :variant="activeTab === tab.key ? 'solid' : 'ghost'"
          size="sm"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </UButton>
      </div>
      <UButton
        :color="activeOnly ? 'primary' : 'neutral'"
        :variant="activeOnly ? 'soft' : 'ghost'"
        size="sm"
        :icon="activeOnly ? 'i-heroicons-check-circle' : 'i-heroicons-clock'"
        @click="activeOnly = !activeOnly"
      >
        {{ activeOnly ? 'Apenas ativas' : 'Todas' }}
      </UButton>
    </div>

    <DealGrid :deals="deals" :loading="loading && deals.length === 0" />

    <div v-if="loading && deals.length > 0" class="flex justify-center py-8">
      <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-primary-500" />
    </div>

    <!-- Sentinel para infinite scroll -->
    <div ref="sentinel" class="h-4" />

    <!-- Sem mais resultados -->
    <div v-if="!hasMore && deals.length > 0" class="text-center py-8 text-sm text-gray-400">
      Você chegou ao fim das ofertas 🎉
    </div>
  </div>
</template>
